// Migration importer — upload a CSV / Excel from ServiceTitan / HouseCallPro /
// Jobber / FieldEdge / QuickBooks, we auto-detect the vendor + suggest a column
// map, owner confirms, we bulk-import.
import React, { useState, useEffect } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Spinner, Select, Field, Input, Tog } from '../ui.jsx';

const STAGES = ['New Lead','Contacted','Qualified','Estimate Sent','Booked','In Progress','Invoiced','Paid','Lost'];

const KINDS = [
  { id:'contacts', label:'Customers', icon:'👤', tip:'Names, phones, addresses' },
  { id:'invoices', label:'Revenue / Invoices',  icon:'💰', tip:'Historical billing — feeds P&L revenue' },
  { id:'expenses', label:'Expenses',  icon:'💸', tip:'Bank / credit card / QuickBooks expense exports' },
  { id:'jobs',     label:'Jobs',      icon:'🔧', tip:'Past + scheduled work' },
];

export function Migration({ user, toast, go }) {
  const [kind, setKind] = useState('contacts');
  const [data, setData] = useState(null); // { headers, rows, vendor, mapping }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Import-time campaign + AI controls. Defaults: bring leads in silent — AI off,
  // workflows paused. Operator then either bulk-enrolls into a specific campaign
  // here, or assigns later from Contacts.
  const [importStage, setImportStage] = useState('New Lead');
  const [aiActive, setAiActive] = useState(false);
  const [wfPaused, setWfPaused] = useState(true);
  const [sourceLabel, setSourceLabel] = useState('');
  const [tagsCsv, setTagsCsv] = useState('');
  const [assignWfId, setAssignWfId] = useState('');
  const [workflows, setWorkflows] = useState([]);

  // Super-admin without an active sub-account context. Instead of forcing them
  // to ghost, let them pick a target sub-account from a dropdown — server
  // accepts `targetAccountId` on /import + /undo for super_admins.
  const isSuperAdminNoGhost = user?.role === 'super_admin' && !user?.accountId;
  const [accounts, setAccounts] = useState([]);
  const [targetAccountId, setTargetAccountId] = useState('');
  const needsAccountContext = isSuperAdminNoGhost && !targetAccountId;

  useEffect(() => {
    if (isSuperAdminNoGhost) {
      api('/api/agency/accounts').then(setAccounts).catch(()=>setAccounts([]));
    }
  }, [isSuperAdminNoGhost]);

  useEffect(() => {
    if (needsAccountContext) return;
    api('/api/workflows').then(setWorkflows).catch(()=>setWorkflows([]));
  }, [needsAccountContext]);

  const parse = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    setBusy(true);
    try {
      let headers, rows;
      if (lower.endsWith('.csv')) {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) throw new Error('Empty CSV');
        const parseRow = (line) => {
          const out = []; let cur = ''; let inQ = false;
          for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
            else if (c === '"') inQ = !inQ;
            else if (c === ',' && !inQ) { out.push(cur); cur = ''; }
            else cur += c;
          }
          out.push(cur); return out;
        };
        headers = parseRow(lines[0]);
        rows = lines.slice(1).map(l => {
          const cells = parseRow(l);
          return Object.fromEntries(headers.map((h, i) => [h, cells[i] ?? '']));
        });
      } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
        if (!window.XLSX) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        const buf = await file.arrayBuffer();
        const wb = window.XLSX.read(buf, { type:'array' });
        rows = window.XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval:'' });
        if (!rows.length) throw new Error('Empty workbook');
        headers = Object.keys(rows[0]);
      } else {
        throw new Error('Unsupported file. Use .csv or .xlsx');
      }
      const { vendor, mapping } = await api('/api/migration/suggest', { method:'POST', body:{ headers, kind }});
      setData({ headers, rows, vendor, mapping });
      setResult(null);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const runImport = async (dryRun = false) => {
    setBusy(true);
    try {
      const options = kind === 'contacts' ? {
        stage: importStage,
        aiActive,
        wfPaused,
        source: sourceLabel.trim() || undefined,
        tagsCsv: tagsCsv.trim() || undefined,
        assignToWorkflowId: assignWfId || undefined,
      } : undefined;
      const body = { kind, rows: data.rows, mapping: data.mapping, dryRun, options };
      if (isSuperAdminNoGhost && targetAccountId) body.targetAccountId = targetAccountId;
      const r = await api('/api/migration/import', { method:'POST', body });
      setResult(r);
      if (!dryRun) {
        const enrolledNote = r.enrolled ? ` · ${r.enrolled} enrolled in workflow` : '';
        toast(`✓ Imported · ${r.created} created · ${r.updated} updated · ${r.skipped} skipped${enrolledNote}`);
      }
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Per-kind undo. Pass `kinds` array to server so we wipe only what was asked,
  // not all 4 collections. Expenses still need the explicit `confirmWipeAllExpenses`
  // flag because they have no migration marker — every expense row would go.
  const undoOneKind = async (kindId) => {
    const labels = { contacts:'imported customers', invoices:'imported invoices', jobs:'imported jobs', expenses:'ALL expenses for this account (no migration marker — includes hand-entered)' };
    if (!confirm(`Wipe ${labels[kindId]}?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const undoBody = { kinds: [kindId] };
      if (kindId === 'expenses') undoBody.confirmWipeAllExpenses = true;
      if (isSuperAdminNoGhost && targetAccountId) undoBody.targetAccountId = targetAccountId;
      const r = await api('/api/migration/undo', { method:'POST', body: undoBody });
      const d = r.deleted || {};
      const n = d[kindId];
      toast(`✓ Removed ${typeof n === 'number' ? n : 0} ${kindId}`);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const undoAll = async () => {
    if (!confirm(`Wipe ALL imported contacts, invoices, jobs AND every expense row for this account?\n\nThis is the nuclear option — even hand-entered expenses get wiped because they have no migration marker.\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const undoBody = { confirmWipeAllExpenses: true };
      if (isSuperAdminNoGhost && targetAccountId) undoBody.targetAccountId = targetAccountId;
      const r = await api('/api/migration/undo', { method:'POST', body: undoBody });
      const d = r.deleted || {};
      toast(`✓ Removed · contacts:${d.contacts ?? 0} · invoices:${d.invoices ?? 0} · jobs:${d.jobs ?? 0} · expenses:${typeof d.expenses==='number'?d.expenses:'(skipped)'}`);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <div style={{ padding:24, maxWidth:880, margin:'0 auto' }}>
    <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>📥 Migrate from another platform</h1>
    <div style={{ color:G.textMuted, fontSize:14, marginTop:6, marginBottom:14 }}>Drop an export from ServiceTitan, HouseCallPro, Jobber, FieldEdge, or QuickBooks. We auto-detect the format, map the columns, and bring everything over.</div>

    {/* One-click API migration — no CSV required */}
    {!needsAccountContext && <OneClickMigrate toast={toast}/>}

    {!needsAccountContext && <div style={{ ...styles.card, padding:12, marginBottom:18, fontSize:12, color:G.textMuted }}>
      <div style={{ marginBottom:8 }}>🗑 <strong>Imported something by mistake?</strong> Wipe just one kind, or everything.</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {KINDS.map(k => (
          <Btn key={k.id} sz="sm" v="danger" onClick={()=>undoOneKind(k.id)} disabled={busy}>{k.icon} Undo {k.label}</Btn>
        ))}
        <div style={{ width:1, alignSelf:'stretch', background:G.border, margin:'0 4px' }}/>
        <Btn sz="sm" v="danger" onClick={undoAll} disabled={busy}>☠️ Undo everything</Btn>
      </div>
    </div>}

    {isSuperAdminNoGhost && <div style={{ ...styles.card, padding:14, marginBottom:18, borderColor:G.purple+'66', background:'rgba(168,85,247,0.06)' }}>
      <div style={{ fontWeight:700, marginBottom:6 }}>🎯 Import into which sub-account?</div>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>
        Super-admin shortcut — pick a target account here and import without ghosting. Data lands in that account's contacts / invoices / jobs / expenses.
      </div>
      <Select value={targetAccountId} onChange={e=>setTargetAccountId(e.target.value)} style={{ width:'100%' }}>
        <option value="">— Select account —</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}{a.slug ? ` (${a.slug})` : ''}</option>)}
      </Select>
      {targetAccountId && <div style={{ fontSize:11, color:G.green, marginTop:8 }}>✓ Importing into: {accounts.find(a=>a.id===targetAccountId)?.name || targetAccountId}</div>}
    </div>}

    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10, marginBottom:18 }}>
      {KINDS.map(k => (
        <button key={k.id} onClick={()=>{ setKind(k.id); setData(null); setResult(null); }} style={{
          ...styles.card, padding:14, textAlign:'left', cursor:'pointer',
          border:`2px solid ${kind===k.id?G.purple:G.cardBorder}`, background: kind===k.id?G.purpleDim:G.card,
        }}>
          <div style={{ fontSize:22 }}>{k.icon}</div>
          <div style={{ fontWeight:700, marginTop:4 }}>{k.label}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{k.tip}</div>
        </button>
      ))}
    </div>

    {!data && <label style={{ ...styles.card, padding:36, textAlign:'center', cursor:'pointer', border:`2px dashed ${G.cardBorder}`, display:'block' }}>
      <div style={{ fontSize:42 }}>📥</div>
      <div style={{ fontSize:16, fontWeight:700, marginTop:8 }}>Drop or click to upload</div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>CSV, XLSX, or XLS · up to 25,000 rows</div>
      <input type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }} onChange={(e)=>parse(e.target.files?.[0])}/>
    </label>}

    {data && <div>
      <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.green+'66', background:G.greenLight }}>
        <strong style={{ color:G.green }}>✓ File parsed</strong> · {data.rows.length} rows · detected vendor: <strong>{data.vendor}</strong>
      </div>

      <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
        <h3 style={{ margin:'0 0 10px 0', fontSize:14 }}>Column mapping</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {Object.entries(getFieldList(kind)).map(([key, label]) => (
            <Field key={key} label={label}>
              <Select value={data.mapping[key]||''} onChange={e => setData({...data, mapping:{...data.mapping, [key]: e.target.value || undefined}})}>
                <option value="">— skip —</option>
                {data.headers.map(h => <option key={h} value={h}>{h}</option>)}
              </Select>
            </Field>
          ))}
        </div>
      </div>

      {kind === 'contacts' && <div style={{ ...styles.card, padding:16, marginBottom:14, borderColor:G.purple+'44' }}>
        <h3 style={{ margin:'0 0 4px 0', fontSize:14 }}>🛡️ Import behavior</h3>
        <div style={{ fontSize:12, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
          By default these leads come in <strong style={{color:G.text}}>silent</strong> — AI does not call, workflows do not fire. Drop them into a specific campaign below, or assign later from Contacts.
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <Field label="Start stage" hint="What pipeline stage these contacts begin at.">
            <Select value={importStage} onChange={e=>setImportStage(e.target.value)}>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Source label (shows on the contact)" hint="e.g. '2024 leads list', 'HCP export', 'old book'.">
            <Input value={sourceLabel} onChange={e=>setSourceLabel(e.target.value)} placeholder="Migration Import"/>
          </Field>
          <Field label="Tags (comma-separated)" hint="Use to filter/segment later. Example: cold-2024, winback-list">
            <Input value={tagsCsv} onChange={e=>setTagsCsv(e.target.value)} placeholder="cold-2024, winback"/>
          </Field>
          <Field label="Assign to workflow / campaign" hint="Optional. All imported contacts get enrolled in this workflow. The workflow respects 'Workflows paused' below — set to off if you want it to fire immediately.">
            <Select value={assignWfId} onChange={e=>setAssignWfId(e.target.value)}>
              <option value="">— don't assign (manual later) —</option>
              {workflows.map(w => <option key={w.id} value={w.id}>{w.name} ({w.status})</option>)}
            </Select>
          </Field>
        </div>

        <div style={{ display:'flex', gap:24, alignItems:'center', padding:'10px 12px', background:G.input, borderRadius:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Tog on={!aiActive} onChange={()=>setAiActive(v=>!v)}/>
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>Don't fire AI on these leads</div>
              <div style={{ fontSize:11, color:G.textMuted }}>Skips speed-to-lead calls + AI auto-replies for imports.</div>
            </div>
          </div>
          <div style={{ width:1, height:36, background:G.cardBorder }}/>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Tog on={wfPaused} onChange={()=>setWfPaused(v=>!v)}/>
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>Workflows paused</div>
              <div style={{ fontSize:11, color:G.textMuted }}>Sequences sit in queue; toggle individual contacts off-pause to start.</div>
            </div>
          </div>
        </div>
      </div>}

      <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
        <Btn onClick={()=>{ setData(null); setResult(null); }} disabled={busy}>← Start over</Btn>
        <Btn onClick={()=>runImport(true)} disabled={busy}>Dry run</Btn>
        <Btn v="primary" onClick={()=>runImport(false)} disabled={busy}>{busy ? <Spinner sz={14}/> : `Import ${data.rows.length}`}</Btn>
      </div>

      {result && <div style={{ ...styles.card, padding:16, marginTop:14, borderColor:G.purple+'66' }}>
        <strong>{result.dryRun ? 'Dry run preview' : 'Import complete'}</strong>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:6 }}>
          <span style={{ color:G.green }}>✓ {result.created} created</span> · <span style={{ color:G.cyan }}>{result.updated} updated</span> · <span style={{ color:G.textMuted }}>{result.skipped} skipped</span>
        </div>
      </div>}
    </div>}
  </div>;
}

function getFieldList(kind) {
  if (kind === 'invoices') return { num:'Invoice #', customer:'Customer', total:'Total $', status:'Status', due:'Due Date', paidAt:'Paid Date' };
  if (kind === 'jobs') return { name:'Job / Service', customer:'Customer', address:'Address', date:'Scheduled Date', startTime:'Start Time', status:'Status', total:'Estimate $', notes:'Notes' };
  if (kind === 'expenses') return { date:'Date *', vendor:'Vendor / Payee', description:'Description / Memo', category:'Category', amount:'Amount *', receiptUrl:'Receipt URL' };
  return { firstName:'First Name', lastName:'Last Name', name:'Full Name (if not split)', phone:'Phone *', email:'Email', address:'Address', city:'City', state:'State', zip:'ZIP', notes:'Notes' };
}

// One-click migration via the source platform's API (no CSV needed).
// Lives at the top of Migration so it's the first thing a switching customer sees.
function OneClickMigrate({ toast }) {
  const [jobberStatus, setJobberStatus] = React.useState(null);
  const [hcpStatus, setHcpStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  React.useEffect(() => {
    api('/api/integrations/jobber/status').then(setJobberStatus).catch(()=>setJobberStatus({connected:false}));
    api('/api/integrations/housecallpro/status').then(setHcpStatus).catch(()=>setHcpStatus({connected:false}));
  }, []);
  const connect = async (provider) => {
    try { const { url } = await api(`/api/integrations/${provider}/connect`); window.open(url, '_blank'); }
    catch(e) { toast(e.message, 'error'); }
  };
  const migrate = async (provider, label) => {
    if (!confirm(`Pull ALL customers + invoices from ${label} into TradeOps now? This is safe — matches by their ID so re-running won't duplicate.`)) return;
    setBusy(provider);
    try {
      const r = await api(`/api/integrations/${provider}/migrate`, { method:'POST' });
      const c = r.customers || r.clients;
      const i = r.invoices;
      toast(`✓ ${label}: ${c?.created||0} customers created, ${c?.updated||0} updated · ${i?.created||0} invoices created, ${i?.updated||0} updated`);
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(null); }
  };
  return <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.green+'66', background:'rgba(34,197,94,0.06)' }}>
    <div style={{ fontWeight:700, marginBottom:4 }}>⚡ One-Click Migration (no CSV needed)</div>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:12 }}>Connect your old platform with OAuth and we'll pull everything via API. Safe to re-run.</div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
      <div style={{ ...styles.card, padding:12, background:G.card }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <strong>Jobber</strong>
          {jobberStatus?.connected ? <Badge c={G.green} bg={G.greenLight}>Connected</Badge> : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
        </div>
        {jobberStatus?.connected
          ? <Btn sz="sm" v="primary" disabled={!!busy} onClick={()=>migrate('jobber', 'Jobber')} style={{ width:'100%' }}>{busy==='jobber'?<Spinner sz={14}/>:'⚡ Pull Everything'}</Btn>
          : <Btn sz="sm" onClick={()=>connect('jobber')} style={{ width:'100%' }}>Connect Jobber</Btn>}
      </div>
      <div style={{ ...styles.card, padding:12, background:G.card }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <strong>HouseCallPro</strong>
          {hcpStatus?.connected ? <Badge c={G.green} bg={G.greenLight}>Connected</Badge> : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
        </div>
        {hcpStatus?.connected
          ? <Btn sz="sm" v="primary" disabled={!!busy} onClick={()=>migrate('housecallpro', 'HouseCallPro')} style={{ width:'100%' }}>{busy==='housecallpro'?<Spinner sz={14}/>:'⚡ Pull Everything'}</Btn>
          : <Btn sz="sm" onClick={()=>connect('housecallpro')} style={{ width:'100%' }}>Connect HouseCallPro</Btn>}
      </div>
    </div>
    <div style={{ fontSize:11, color:G.textMuted, marginTop:10 }}>Don't see your old platform? Drop a CSV/Excel export below — we auto-detect format.</div>
  </div>;
}
